import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { User } from 'firebase/auth'
import {
    fetchLoggedUser,
    login,
    updateUserPassword,
} from '../../../components/shared/auth'
import { database } from '../../../components/shared/database'
import { useAppSelector } from '../../../hooks/redux'

interface AuthedUser {
    uid: string
    email: string
}

/**
 * Logged-user password change form.
 *
 * Re-authenticates with the current password before mutating the
 * password and updates the `defaultPasswordChanged` flag in firestore.
 */
const UserPasswordChange = () => {
    const { t } = useTranslation()
    const authedUser = useAppSelector(state => state.auth.user) as AuthedUser | null

    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [newPasswordRepeat, setNewPasswordRepeat] = useState('')
    const [error, setError] = useState<string | null>(null)

    const submitForm = (): void => {
        if (!authedUser) {
            return
        }

        if (currentPassword === '' || newPassword === '' || newPasswordRepeat === '') {
            setError('auth/fill-all')
            return
        }

        if (newPassword !== newPasswordRepeat) {
            setError('auth/passwords-not-match')
            return
        }

        login(authedUser.email, currentPassword)
            .then((currentUser: User | null) => {
                if (!currentUser) {
                    return
                }
                return updateUserPassword(currentUser, newPassword)
                    .then(() => (
                        database('user')
                            .doc(currentUser.uid)
                            .update({
                                defaultPasswordChanged: true,
                            })
                            .then(() => fetchLoggedUser())
                    ))
            })
            .catch((err: Error) => setError(err.message))
    }

    return (
        <div>
            <div className="form-group">
                <label htmlFor="password-form-current">
                    {t('auth.currentPassword')}
                </label>
                <input
                    type="password"
                    value={currentPassword}
                    onChange={({ target }) => {
                        setCurrentPassword(target.value)
                        setError(null)
                    }}
                    className="form-control"
                    id="password-form-current"
                    placeholder={t('auth.currentPassword')}
                />
            </div>
            <div className="form-group">
                <label htmlFor="password-form-new">
                    {t('auth.newPassword')}
                </label>
                <input
                    type="password"
                    value={newPassword}
                    onChange={({ target }) => {
                        setNewPassword(target.value)
                        setError(null)
                    }}
                    className="form-control"
                    id="password-form-new"
                    placeholder={t('auth.newPassword')}
                />
            </div>
            <div className="form-group">
                <label htmlFor="password-form-new-repeat">
                    {t('auth.newPasswordRepeat')}
                </label>
                <input
                    type="password"
                    value={newPasswordRepeat}
                    onChange={({ target }) => {
                        setNewPasswordRepeat(target.value)
                        setError(null)
                    }}
                    className="form-control"
                    id="password-form-new-repeat"
                    placeholder={t('auth.newPasswordRepeat')}
                />
            </div>

            {error && (
                <div className="my-3 text-danger font-weight-bold">
                    {t(error)}
                </div>
            )}

            <div className="form-group row">
                <div className="col-sm-10">
                    <button
                        type="button"
                        className="btn btn-success"
                        onClick={submitForm}
                        disabled={!!error}
                    >
                        {t('auth.passwordChange')}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default UserPasswordChange
